/**
 * CLI handler for triggering a workflow execution.
 *
 * Usage:
 *   canary workflow run <workflowId> [options]
 *   --cache-only waits for the run to reach a terminal status.
 *
 * @module WorkflowRunCLI
 */

import process from 'node:process';
import { getArgValue, hasFlag } from './auth.js';
import {
  apiRequest,
  fetchWorkflowEnvironments,
  renderWorkflowUserError,
  resolveEnvironmentByNameOrId,
} from './cli-helpers.js';
import { statusIcon } from './cli-format.js';
import { formatCacheOnlyFailure, getCacheOnlyFailure } from './cache-only-failure.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ExecuteResponse = {
  ok: boolean;
  error?: string;
  runId: string;
  streamId: string;
};

type RunStatusResponse = {
  ok: boolean;
  error?: string;
  data: {
    run: {
      id: string;
      status: string;
      startedAt: string | null;
      finishedAt: string | null;
      errorMessage: string | null;
      metadataJson: Record<string, unknown> | null;
    };
  };
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ---------------------------------------------------------------------------
// Handler
// ---------------------------------------------------------------------------

export async function handleWorkflowRun(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  // `--environment` takes a value; exclude that token when picking the
  // positional workflow id so `run --environment staging <wf>` still works.
  const environmentArg = getArgValue(argv, '--environment');
  const claimScopeArg = getArgValue(argv, '--claim-scope');
  const browserModeArg = getArgValue(argv, '--browser-mode');
  const flagValues = new Set(
    [environmentArg, claimScopeArg, browserModeArg].filter((v): v is string => Boolean(v))
  );
  const workflowId = argv.find((a) => !a.startsWith('-') && !flagValues.has(a));
  if (!workflowId) {
    console.error('Error: Missing workflow ID.');
    console.error(
      'Usage: canary workflow run <workflowId> [--environment <name|id>] [--browser-mode <headed|headless>] [--cache-only] [--stream] [--json]'
    );
    console.error('  --cache-only  Replay cached actions only and wait for completion.');
    process.exit(1);
  }

  const jsonOutput = hasFlag(argv, '--json');
  const stream = hasFlag(argv, '--stream');
  const cacheOnly = hasFlag(argv, '--cache-only');
  const waitForTerminal = stream || cacheOnly;
  const requestBody: Record<string, unknown> = {
    triggerSource: 'cli_direct',
  };
  if (cacheOnly) {
    requestBody.cacheOnly = true;
  }
  if (claimScopeArg) {
    requestBody.claimScope = claimScopeArg;
  }
  if (browserModeArg) {
    if (browserModeArg !== 'headed' && browserModeArg !== 'headless') {
      console.error('Error: --browser-mode must be headed or headless.');
      process.exit(1);
    }
    requestBody.browserMode = browserModeArg;
  }

  // Optional environment override. Resolve a name/id against the flow's
  // property and pass `environmentId` to the execute endpoint. When omitted the
  // server falls back to the workflow's configured `defaultEnvironmentId` (then
  // credential-bound / property-default env), so a plain run targets the
  // sandbox once the flow's default is set via `workflow patch`.
  let resolvedEnvironmentId: string | undefined;
  if (environmentArg) {
    const envs = await fetchWorkflowEnvironments(apiUrl, token, workflowId);
    resolvedEnvironmentId = resolveEnvironmentByNameOrId(envs, environmentArg);
    requestBody.environmentId = resolvedEnvironmentId;
  }

  // Trigger execution
  const result = await apiRequest<ExecuteResponse>(
    apiUrl,
    token,
    'POST',
    `/workflows/${workflowId}/execute`,
    requestBody
  );

  if (!result.ok) {
    if (jsonOutput) {
      console.log(JSON.stringify(result, null, 2));
    } else {
      const rendered = renderWorkflowUserError(result as unknown as Record<string, unknown>);
      console.error(rendered ?? `Error: ${result.error ?? 'Unknown error'}`);
    }
    process.exit(1);
  }

  const { runId } = result;

  if (jsonOutput && !waitForTerminal) {
    console.log(JSON.stringify({ ok: true, runId, streamId: result.streamId }, null, 2));
    return;
  }

  if (!waitForTerminal) {
    console.log(`Run triggered successfully.`);
    console.log(`  Run ID: ${runId}`);
    if (environmentArg) {
      console.log(`  Environment: ${environmentArg} (${resolvedEnvironmentId})`);
    }
    console.log(`\nCheck status: canary workflow runs get ${workflowId} ${runId}`);
    return;
  }

  // Stream and cache-only modes poll run status until completion.
  if (!jsonOutput) {
    console.log(`Run triggered (${runId}). Waiting for completion...\n`);
  }

  const terminalStatuses = new Set([
    'completed',
    'success',
    'passed',
    'failed',
    'inconclusive',
    'canceled',
    'timeout',
  ]);
  let lastStatus = '';
  let firstPoll = true;

  for (;;) {
    if (!firstPoll) await sleep(3000);
    firstPoll = false;

    const status = await apiRequest<RunStatusResponse>(
      apiUrl,
      token,
      'GET',
      `/workflows/${workflowId}/runs/${runId}`
    );

    if (!status.ok) {
      if (jsonOutput) {
        console.log(JSON.stringify(status, null, 2));
      } else {
        console.error(`Error polling status: ${status.error ?? 'Unknown error'}`);
      }
      process.exit(1);
    }

    const run = status.data.run;

    if (cacheOnly && run.metadataJson?.cacheOnly !== true) {
      const isTerminal = terminalStatuses.has(run.status);
      const cancellation = isTerminal
        ? null
        : await apiRequest<{ ok: boolean; error?: string }>(
            apiUrl,
            token,
            'POST',
            `/workflows/${workflowId}/runs/${runId}/cancel`,
            {}
          );
      const cancellationMessage = isTerminal
        ? 'The run was already terminal before it could be canceled.'
        : cancellation?.ok
          ? 'The run was canceled before polling continued.'
          : `Automatic cancellation failed: ${cancellation?.error ?? 'unknown error'}`;
      const error =
        'Cache-only safety check failed: the created run did not persist cacheOnly=true. ' +
        cancellationMessage +
        ' Cancellation acknowledgment does not prove the run is quiescent; wait for its full job and agent tree to stop before retrying or restoring cache rows.';

      if (jsonOutput) {
        console.log(
          JSON.stringify(
            { ok: false, runId, streamId: result.streamId, status: run.status, error },
            null,
            2
          )
        );
      } else {
        console.error(`Error: ${error}`);
        console.error(`Run ID: ${runId}`);
      }
      process.exit(1);
    }

    if (!jsonOutput && run.status !== lastStatus) {
      console.log(`  ${statusIcon(run.status)} ${run.status}`);
      lastStatus = run.status;
    }

    if (terminalStatuses.has(run.status)) {
      const cacheOnlyFailure = getCacheOnlyFailure(run.metadataJson);

      if (jsonOutput) {
        console.log(
          JSON.stringify(
            {
              ok: !['failed', 'timeout', 'canceled'].includes(run.status),
              runId,
              streamId: result.streamId,
              status: run.status,
              error: run.errorMessage,
              ...(cacheOnlyFailure ? { cacheOnlyFailure } : {}),
            },
            null,
            2
          )
        );
      } else {
        if (run.errorMessage) {
          console.log(`\nError: ${run.errorMessage}`);
        }
        if (run.status === 'failed' && cacheOnlyFailure) {
          console.log('');
          for (const line of formatCacheOnlyFailure(cacheOnlyFailure)) {
            console.log(line);
          }
        }
        console.log(`\nRun finished: ${run.status}`);
        console.log(`Details: canary workflow runs get ${workflowId} ${runId}`);
      }

      if (run.status === 'inconclusive') {
        process.exit(2);
      }
      if (run.status === 'failed' || run.status === 'timeout' || run.status === 'canceled') {
        process.exit(1);
      }
      return;
    }
  }
}
